import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './Subcategories.css';
import { SubcategoryArrow } from '../SubcategoryArrow';
import { SubcategoryItem } from '../SubcategoryItem';
import { useScrollPosition } from '../../../hooks/use-scroll-position.hook';
import { SubcategoryItemLoader } from '../SubcategoryItemLoader';
import { verticalActionTypesEnum } from '../../../../../shared/src/enums/vertical-types.enum';
import { SubcategoriesSearch } from '../../../../../shared/src/components/Search/SubcategoriesSearch';
import { SubcategoriesActions } from '../SubcategoriesActions';
import { subcategoryTypesEnum } from '../../../../../shared/src/enums/subcategory-types';
import { useSortingOptionAvailability } from '../../../hooks/use-sorting-option-availability';
import { useFilterOptionAvailability } from '../../../hooks/use-filter-option-availability.hook';
import { useDeviceTypeHook } from '../../../../../shared/src/hooks/use-device-type.hook';

export const Subcategories = ({
    className = '',
    subcategories,
    currentSubcategoryId,
    setCurrentSubcategoryId,
    // can be used for controlling subcategories loader from outside
    gridContentLoaded = true,
    currentSubcategory,
    verticalRef,
}) => {
    const [portal, setPortal] = useState(null);
    const scrollableItemsElement = useRef();
    const [leftArrowVisible, setLeftArrowVisible] = useState(true);
    const [rightArrowVisible, setRightArrowVisible] = useState(true);
    const { isMobile } = useDeviceTypeHook();
    const { isAnySortingOptionAvailable } = useSortingOptionAvailability();
    const { isAnyFilterOptionAvailable } = useFilterOptionAvailability();
    const showActions = currentSubcategory?.type === subcategoryTypesEnum.CATEGORY
        && (isAnySortingOptionAvailable || isAnyFilterOptionAvailable);

    // get and save target element for portal
    useEffect(() => {
        const subCategoriesPortalElement = document.getElementById('asg-vertical-subcategories-portal');

        if (subCategoriesPortalElement) {
            setPortal(subCategoriesPortalElement);
        }
    }, []);

    // arrows functionality
    useScrollPosition(
        scrollableItemsElement,
        (positionKey) => {
            switch (positionKey) {
            case 'start':
                setLeftArrowVisible(false);
                break;
            case 'middle':
                setLeftArrowVisible(true);
                break;
            case 'scrollable':
                setRightArrowVisible(true);
                break;
            case 'end':
                setRightArrowVisible(false);
                break;
            }
        },
        [portal, subcategories]
    );

    const allSubcategoriesIds = useMemo(() => (() => {
        return subcategories.map((subcategory) => (subcategory.id));
    })(), [subcategories]);

    if (!portal) {
        return null;
    }

    const arrowClickHandler = (scrollByPx) => {
        scrollableItemsElement.current.scrollBy({
            top: 0,
            left: scrollByPx,
            behavior: 'smooth'
        });
    };

    const isSubcategoryActive = (subcategoryIdToCheck, subcategoryIdToCheckIndex, currentSelectedSubcategoryId) => {
        const isIdEqualsCurrent = subcategoryIdToCheck === currentSelectedSubcategoryId;

        // handle case when currentSelectedSubcategoryId is virtual
        // and does not exist in subcategories set. Select first subcategory in this case
        if (!isIdEqualsCurrent && !allSubcategoriesIds.includes(currentSelectedSubcategoryId)) {
            return subcategoryIdToCheckIndex === 0;
        }

        return isIdEqualsCurrent;

    };

    // portal needed for rendering subcategories higher in html for proper "position: sticky" work
    return ReactDOM.createPortal(
        (
            <div className={`${className} asg-subcategories`}>
                {/*left arrow*/}
                {leftArrowVisible && <SubcategoryArrow
                    arrowPosition="left"
                    clickHandler={() => {arrowClickHandler(-100);}}
                /> }

                {/*subcategories*/}
                <div className="asg-subcategories__scrollable" ref={scrollableItemsElement}>
                    <div className="asg-subcategories__scrollable-items">
                        {isMobile && <SubcategoriesSearch />}
                        {!gridContentLoaded && <SubcategoryItemLoader />}

                        {gridContentLoaded && subcategories.map((subcategory, subcategoryIndex) => {
                            if (subcategory.action === verticalActionTypesEnum.PLATFORM_CATEGORIES) {
                                return <SubcategoryItemLoader key={subcategory.id} />;
                            }

                            return (
                                <SubcategoryItem
                                    key={subcategory.id}
                                    subcategory={subcategory}
                                    isActive={isSubcategoryActive(
                                        subcategory.id,
                                        subcategoryIndex,
                                        currentSubcategoryId
                                    )}
                                    setCurrentSubcategoryId={setCurrentSubcategoryId}
                                />
                            );
                        })}
                        {/*this hack enables right padding on mobile with flex + overflow scroll*/}
                        <div className="asg-subcategories__item--fake">&nbsp;</div>
                    </div>
                </div>

                {/*right arrow*/}
                {rightArrowVisible && <SubcategoryArrow
                    arrowPosition="right"
                    clickHandler={() => {arrowClickHandler(100);}}
                />}

                {showActions && <SubcategoriesActions 
                    verticalRef={verticalRef}
                    scrollableItemsElement={scrollableItemsElement}
                    currentSubcategory={currentSubcategory}
                />}
            </div>
        ),
        portal
    );
};
